import {
  AnmeldelseType,
  BedrageriMetodeTypeSvar,
  DatakrimSvarType,
  DatakrimType,
  EgneTiltakType,
  ElektronikkType,
  HandlingType,
  HarBankenStansetOverforingType,
  HendelsesstedValg,
  KjennemerkeKategori,
  KjennemerketypeType,
  KonfliktraadSvarType,
  OverforingFormaalType,
  OverforingType,
  SamtykkeVirksomhetKanSeAnmeldelsen,
  SkadeverkGjenstandType,
  TilbakeforingType,
  TilegnetPersoninformasjonVerdiType,
  TilegnetVerdiFysiskIdType,
  TilegnetVerdiType,
  ExperimentalTypeKontaktopplysning,
  TyveriGjenstandType,
} from "@politiet/anmeldelse-lib/types/anmeldelseUtkast.typer";

import {
  AnmeldelseType as DigaAnmeldelseType,
  BrukstyveriType,
  GjerningsstedType,
  SkadeverkType,
} from "@politiet/anmeldelse-lib/types/digaAnmeldelseBegrenset.typer";

import { KonfliktraadSvarType as SkjemaKonfliktraadSvarType } from "@politiet/anmeldelse-lib/types/anmeldelseSkjema.typer";
import { SVVFargekode } from "../../skjema/anmeldelse/gjerning/Handlinger/gjenstander/components/felles/kjoretoy/SVVFargekoder.typer";
import { GjerningspersonFragmentRequiredTexts } from "../../skjema/anmeldelse/gjerning/Gjerningsaktor/Gjerningsperson/GjerningspersonFragment";

const skjematekst = {
  felles: {
    anmeldelseType: {
      TYVERI_SKADEVERK_INNBRUDD: "Tyveri, innbrudd og skadeverk",
      BEDRAGERI: "Bedrageri",
    } satisfies Record<AnmeldelseType, string>,
    stegvelger: {
      ferdig: {
        ariaLabel: "Steg fullført.",
      },
      navbar: {
        ariaLabel: "Stegviser",
      },
    },
    virksomhet: {
      orgnummer: "Organisasjonsnummer",
      valgtVirksomhet: {
        slett: "Slett",
        beliggenhetsadresse: "Beliggenhetsadresse",
        aktivitet: "Aktivitet",
      },
      slettVirksomhet: "Slett virksomhet",
    },
    knapper: {
      rediger: {
        label: "Endre",
        ariaLabel: "Endre",
        ariaLabelIndexed: "Endre nummer {{nr}}",
      },
      lukk: {
        ariaLabel: "Lukk",
      },
    },
    feilmelding: {
      ikon: {
        ariaLabel: "Feilmelding",
      },
      sum: {
        kunTallITallfelt:
          "Dette feltet kan kun inneholde tall. Andre tegn som komma, punktum og bokstaver er ikke tillatt. Rund av til nærmeste hele tall om du forsøker å skrive et desimaltall",
      },
    },
    components: {
      pagination: {
        forrige: {
          ariaLabel: "Forrige side",
        },
        numerertSide: {
          ariaLabel: "Gå til side {{side}}",
        },
        neste: {
          ariaLabel: "Neste side",
        },
      },
      sokefelt: {
        ariaLabel: "Søk",
      },
      modal: {
        ariaLabel: "Lukk vindu",
      },
      erstatningskrav: {
        ariaLabel: "Erstatningskrav dokumentasjon",
      },
      handelsvarer: {
        slett: {
          ariaLabel: "Slett gjenstand",
        },
      },
      autocomplete: {
        ingenTreff: "Ingen treff",
        laster: "Laster...",
      },
      inputDate: {
        toggleDatoVelgerKnapp: {
          closed: "Åpne datovelger",
          open: "Lukk datovelger",
        },
        placeholder: "dd.mm.åååå",
        errorMessages: {
          parseError: "Klarte ikke tyde dato, bruk formatet dd.mm.åååå",
        },
        lukkKnapp: "Fullfør",
        labels: {
          labelDayButton_today: "I dag, {{label}}",
          labelDayButton_selected: "{{label}} valgt",
          labelWeekNumber: "Uke {{weekNumber}}",
          labelNext: "Neste måned",
          labelPrevious: "Forrige måned",
          labelMonthDropdown: "Velg måned",
          labelYearDropdown: "Velg år",
        },
      },
      inputDateTime: {
        timePicker: {
          timeLabel: "Time",
          timeAriaLabel: "Velg time",
          minuttLabel: "Minutt",
          minuttAriaLabel: "Velg minutt",
        },
        placeholder: "dd.mm.åååå tt:mm",
        errorMessages: {
          parseError: "Klarte ikke tyde dato, bruk formatet dd.mm.åååå tt:mm",
        },
      },
    },
  },
  feilside: {
    notFound: {
      tittel: "Beklager, denne siden finner vi ikke",
      info: "Det kan hende du må logge inn for å se siden, eller så kan det hende den ikke finnes. Har du sjekket at adressen er skrevet riktig?",
      infoInnlogget: "Har du sjekket at adressen er skrevet riktig?",
    },
    forbidden: {
      tittel: "Du har ikke tilgang til å se denne siden",
      info: "Har du sjekket at adressen er skrevet riktig?",
    },
    default: {
      tittel: "Noe feil har skjedd",
      info: "Last inn siden på nytt eller prøv igjen senere",
    },
    knapper: {
      tjenester: "Se politiets tjenester",
      forsiden: "Gå til forsiden for anmeldelser",
      logginn: "Logg inn",
      lastInn: "Last inn siden på nytt",
    },
  },
  anmeldelse: {
    autoslettMelding_one: "Utkastet blir slettet om {{count}} dag",
    autoslettMelding_other: "Utkastet blir slettet om {{count}} dager",
    av_anmeldelse_nummer: "av anmeldelse nummer",
    chips: {
      INNBRUDD: "Innbrudd",
      SKADEVERK: "Skadeverk",
      TYVERI: "Tyveri",
      BEDRAGERI: "Bedrageri",
      DATAKRIM: "Datakriminalitet",
    } satisfies Record<HandlingType, string>,
    fornaermede_mangler: "Fornærmede mangler",
    lovbrudd_mangler: "-- Lovbrudd mangler --",
    fortsett_utfylling: "Fortsett utfyllingen",
    fra: "fra",
    gaa_til_anmeldelse: "Gå til anmeldelse",
    gjerningTyper: {
      INNBRUDD: "Innbrudd",
      SKADEVERK: "Skadeverk",
      TYVERI: "Tyveri",
    },
    ingen_anmeldelser_innsendt: "--- Ingen innsendte anmeldelser ---",
    ingen_anmeldelser_paabegynt: "--- Ingen påbegynte anmeldelser ---",
    kort: {
      diga: {
        chip: "Eldre anmeldelse",
        type: {
          TYVERI_AV_SYKKEL: "Tyveri av sykkel",
          TYVERI_AV_MOBILTELEFON: "Tyveri av mobiltelefon",
          TYVERI_FLERE_GJENSTANDER: "Tyveri av andre gjenstander",
          KJENNEMERKE: "Tyveri av kjennemerke (registreringsskilt)",
          SKADEVERK: "Skadeverk på privat eiendom, gjenstander eller kjøretøy",
          KRENKELSE: "Ulovlig bruk av fast eiendom",
          BRUKSTYVERI: "Brukstyveri av kjøretøy eller båt fra privatperson",
        } satisfies Record<DigaAnmeldelseType, string>,
      },
      skjema: {
        SKADEVERK: {
          gjenstand: {
            label_zero: "Skadeverk",
            label_one:
              "Skadeverk på $t(anmeldelse.kort.skjema.gjenstandTyper.{{gjenstandType}})",
            label_other: "Skadeverk på flere gjenstander",
          },
        },
        TYVERI: {
          gjenstand: {
            label_zero: "Tyveri",
            label_one:
              "Tyveri av $t(anmeldelse.kort.skjema.gjenstandTyper.{{gjenstandType}})",
            label_other: "Tyveri av flere gjenstander",
          },
        },
        gjenstandTyper: {
          ANNET: "Annet",
          FARTOY: "Fartøy",
          HANDELSVARER: "Gjenstandsliste",
          KJENNEMERKE: "Registreringsskilt (kjennemerke)",
          KJOERETOEY: "Kjøretøy",
          MOBILTELEFON: "Mobiltelefon",
          SYKKEL: "Sykkel",
          ELEKTRONIKK: "Elektronikk",
          BYGNING_EIENDOM: "Bygning eller eiendom",
          VERKTOY: "Verktøy",
        } satisfies Record<
          TyveriGjenstandType | SkadeverkGjenstandType,
          string
        >,
      },
      og: "og",
    },
    nummer: "nummer",
    saksnummer: "Saksnummer",
    sendt_inn: "Sendt inn",
    sist_endret: "Sist endret",
    statusTyper: {
      UTKAST: "Utkast",
    },
  },
  auth: {
    min_profil: {
      ariaLabel: "Informasjon om din bruker",
      kontaktInfo: "Kontaktinformasjon til politiet",
      knapper: {
        logg_ut: "Logg ut",
      },
    },
    begrensetTilgang: {
      innlogget: {
        tittel: "Du har ikke tilgang til å anmelde digitalt",
        isNotInnloggetMedGjeldendePid: {
          info: "Du er innlogget med en utgått identifikator. Du må logge inn med identifikatoren som er gjeldende i folkeregisteret",
        },
      },
    },
    erDuHerFortsatt: {
      forbliKnapp: "Forbli innlogget",
      info: "Du har vært stille en stund. Hvis du ikke gjør noe på denne siden blir du snart logget ut.",
      loggUtKnapp: "Logg ut nå",
      tittel: "Er du her fortsatt?",
    },
    idPortenBar: {
      kontaktPolitiet: "Kontakt politiet",
      loggetInnSom: "Logget inn som",
      nodnummer: "Nødnummer",
      ringPolitiet: "Ring politiet",
    },
    representasjon: {
      altinnFeil: "Kunne ikke hente roller fra Altinn",
      fnr: "f. nr: {{fnr, fnr}}",
      ingenAaRepresenetere:
        "Basert på roller i Altinn kan du ikke representere noen andre enn deg selv",
      megSelv: "(meg selv)",
      orgnr: "org. nr: {{orgnr, orgnr}}",
      velgHvemDuRepresenterer: "Velg hvem du vil representere",
      virksomheter: "Virksomheter",
    },
    utloggetSide: {
      automatiskInfo:
        "Du var stille en stund. For å ivareta sikkerheten din, ble du automatisk logget ut. Anmeldelsen er lagret, og du kan fortsette å fylle den ut senere",
      forsiden: "Gå til forsiden",
      logginn: "Logg inn igjen",
      tittel: "Du har blitt logget ut",
    },
  },
  erstatningskrav: {
    beskrivelseErstatningskrav: {
      helptext: "Hva er tapet? Hvorfor skal dette erstattes?",
      label: "Beskriv hva det kreves erstatning for",
    },
    dokumentasjonVedlegg: {
      label:
        "Last opp dokumentasjon på at fornærmede har rett til erstatning (valgfritt)",
      label_ANMELDER:
        "Last opp dokumentasjon på at du har rett til erstatning (valgfritt)",
      label_PERSON:
        "Last opp dokumentasjon på at fornærmede har rett til erstatning (valgfritt)",
      label_VIRKSOMHET:
        "Last opp dokumentasjon på at virksomheten har rett til erstatning (valgfritt)",
      helpText:
        "For eksempel kvitteringer av det som er tapt eller bilder av det som er ødelagt",
    },
    fullmaktVedlegg: {
      label_PERSON: `Last opp fullmakt til å reise erstatningskrav for fornærmede (valgfritt)`,
      label_VIRKSOMHET: `Last opp fullmakt til å reise erstatningskrav for virksomhet (valgfritt)`,
      helpText: "Verger, eiere, styreledere og liknende trenger ikke fullmakt",
    },
    infoErstatningskrav: {
      bulletpoints: [
        "Hvis saken fører til bot eller rettssak, kan politiet kreve erstatning for ting som er stjålet eller ødelagt. Erstatningen går til personen eller virksomheten som har hatt tapet. Dersom du får tapet ditt dekket av forsikringsselskapet, er det forsikringsselskapet som har krav på erstatning.",
        "For at vi skal kunne kreve erstatning for deg må du dokumentere og forklare:",
        "BULLET_START",
        "Hva som er stjålet eller ødelagt",
        "Hva tingene er verdt",
        "På hvilken måte tapet er knyttet til hendelsen",
        "BULLET_END",
        "Dokumentasjon kan for eksempel være kvitteringer, fakturaer, bilder eller prisoverslag på reparasjon.",
        "Hvis du anmelder for en annen person eller virksomhet må du ha en fullmakt som viser at du har rett til å kreve erstatning på vegne av fornærmede. En fullmakt skal inneholde informasjon om hvem som gir den, hvem som får den og hva det gis fullmakt til å gjøre. Den bør være datert og signert.",
      ],
    },
    ja: "Ja",
    nei: "Nei",
    radio1: "Ønsker fornærmede å reise erstatningskrav?",
    radio1_ANMELDER: "Ønsker du å reise erstatningskrav?",
    radio1_PERSON: "Ønsker fornærmede å reise erstatningskrav?",
    radio1_VIRKSOMHET: "Ønsker virksomheten å reise erstatningskrav?",
    radioOptions: {
      JA: "Ja",
      NEI: "Nei",
      VET_IKKE: "Vet ikke",
    },
    se_over: {
      dokumentasjonVedlegg:
        "Dokumentasjon på at fornærmede har rett til erstatning",
      dokumentasjonVedlegg_ANMELDER:
        "Dokumentasjon på at du har rett til erstatning",
      dokumentasjonVedlegg_PERSON:
        "Dokumentasjon på at fornærmede har rett til erstatning",
      dokumentasjonVedlegg_VIRKSOMHET:
        "Dokumentasjon på at virksomheten har rett til erstatning",
      fullmaktVedlegg_PERSON: "Fullmakt til å reise erstatning for fornærmede",
      fullmaktVedlegg_VIRKSOMHET:
        "Fullmakt til å reise erstatning for virksomheten",
    },
    vet_ikke: "Vet ikke",
  },
  filtrering: {
    filtreringsType: {
      kategori: {
        options: {
          INNBRUDD: {
            label: "Innbrudd",
          },
          SKADEVERK: {
            label: "Skadeverk",
          },
          TYVERI: {
            label: "Tyveri",
          },
          BEDRAGERI: {
            label: "Bedrageri",
          },
        },
        section: {
          label: "Kategori",
        },
      },
      tidsrom: {
        options: {
          FRA_DATO: {
            label: "Fra dato",
          },
          TIL_DATO: {
            label: "Til dato",
          },
        },
        section: {
          label: "Tidsrom",
        },
      },
    },
    mobilknapp: {
      label: "Filtrer",
      labelMedFilterOne: "{{antall}} aktivt filter",
      labelMedFilterOther: "{{antall}} aktive filtere",
    },
  },
  forside: {
    tittel: "Anmelde digitalt",
    spraakvelger: {
      ariaLabel: "Velg språk",
    },
    tabContainer: {
      anmelde: "Anmelde",
      utkast: "Utkast",
      innsendt: "Innsendt",
    },
    info: {
      title: "Informasjon og veiledning",
      tipsUtfylling: {
        title: "Tips til deg som skal fylle ut en anmeldelse",
        bulletpoints: [
          "Ha gjerne dokumentasjon og vedlegg klare før du starter. Eksempler på dokumentasjon kan være videoovervåking, bilder av stjålne gjenstander, kvitteringer, kontoutskrifter eller skjermbilder av kommunikasjon mellom deg og gjerningspersonen.",
          "Anmeldelsen består av 8 steg, og du kan fylle inn i den rekkefølgen du ønsker. Det kan være lurt å bla igjennom alle stegene før du begynner, slik at du forstår hvilke opplysninger som passer hvor.",
          "For at politiet skal kunne reagere på lovbruddet, er det viktig med gode beskrivelser av hendelsen og så mange opplysninger som mulig.",
          "Noen opplysninger vil være viktige å fylle inn selv om de ikke er påkrevd, for eksempel serienummer på stjålne gjenstander og fødselsnummer på involverte personer hvis du har dette.",
          "Dersom flere gjerningspersoner er involvert i samme lovbrudd, skal det kun leveres én anmeldelse. Det er da særlig viktig med god beskrivelse av hva hver enkelt har gjort og hvorfor hen mistenkes for det aktuelle lovbruddet.",
        ],
        veileder: {
          lenke: "Les mer i veileder for digital anmeldelse",
        },
      },
      nyeOpplysningerOmAnmeldtForhold: {
        title: "Nye opplysninger om anmeldt forhold?",
        tekst: [
          "Hvis du ønsker å trekke tilbake anmeldelsen eller gi oss flere opplysninger om saken,",
          "kan du kontakte politidistriktet der hendelsen skjedde og oppgi saksnummeret du ",
          "fikk da du sendte inn anmeldelsen. Da kan vi oppdatere eller trekke tilbake",
          "anmeldelsen din. Du finner også saksnummer til alle dine nettanmeldelser under",
          '"innsendte" på denne siden.',
        ],
        veileder: {
          lenke: "Les mer om ettersending og finn riktig politistasjon",
        },
      },
    },

    beskrivelse: {
      omAaAnmelde: {
        infotekst: {
          info: "I anmeldelsesportalen kan du anmelde lovbruddene tyveri, skadeverk, innbrudd, bedrageri og datakriminalitet digitalt. Du kan også se dine innsendte anmeldelser eller fortsette på anmeldelser du har begynt å fylle ut.",
          andreKrimTyper:
            "Skal du anmelde andre typer kriminalitet, må du kontakte ditt nærmeste politidistrikt.",
        },
      },
      dineAnmeldelser: {
        tittel: "Dine anmeldelser",
        innsendteAnmeldelser: {
          tittel: "Innsendte anmeldelser",
          badRequest:
            "Tilgangskontroll feilet. Det er ikke åpnet for å se innsendte anmeldelser på vegne av andre personer.",
          info: "Her vises anmeldelser som du har sendt inn på politiet.no. Hvis du har spørsmål til anmeldelser som ble sendt inn på andre måter, ber vi deg ta kontakt med ditt nærmeste politi.",
          infoRepresentasjon:
            "Her vises anmeldelser som er sendt inn på vegne av virksomheten på politiet.no. Hvis du har spørsmål til anmeldelser som ble levert på andre måter, ber vi deg ta kontakt med ditt nærmeste politi.",
          infoTom:
            "Her er det ingen innsendte anmeldelser. Hvis du leter etter en anmeldelse du har sendt inn tidligere, ber vi deg kontakte ditt nærmeste politi.",
          manglerTilgang:
            "Du har ikke tilgang til å se innsendte anmeldelser på vegne av {{ valgtPartId }}. Daglig leder skal ha tilgang, og kan også delegere tilgangen til andre i Altinn.",
        },
        utkast: {
          tittel: "Dine utkast",
          info: "Her er anmeldelser som du har startet på, men som ikke er sendt inn ennå.",
          tomt: "Du har ingen påbegynte anmeldelser.",
        },
      },
    },
    knapper: {
      opprettAnmeldelse: {
        tyveriSkadeverkInnbrudd: "Anmeld tyveri, innbrudd og skadeverk",
        bedrageri: "Anmeld bedrageri (svindel) og datakriminalitet",
      },
      loggInn: "Logg inn",
    },
    eksisterendeAnmeldelseUtkast: {
      modal: {
        tittel: "Vil du fortsette på utkastet ditt?",
        beskrivelse:
          "Du har allerede begynt på en anmeldelse av {{anmeldelseType}}. Ønsker du å fortsette på den?",
        fortsettUtfylling: "Fortsett utfylling",
        opprettNy: "Opprett ny anmeldelse",
      },
    },
  },
  hvaHarSkjedd: {
    felles: {
      gjenstander: {
        forsikring: {
          title: "Forsikringsinformasjon",
          radioSpoersmaal: "Skal gjenstanden erstattes av forsikring?",
          radioOptions: {
            JA: "Ja",
            NEI: "Nei",
          },
          polisenummer: {
            label: "Forsikringsnummer (polisenummer)",
          },
          forsikringsselskap: {
            se_over_label: "Forsikringsselskap",
          },
          sok: {
            label: "Forsikringsselskap",
            helpText:
              "Søk opp navn eller organisasjonsnummer. Undersøk forsikringsavtalen din hvis du er usikker på hvem som er leverandør.",
            feilmeldingBrreg:
              "Kunne ikke hente forsikringsselskap fra Brønnøysundregistrene",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
            label: "Spesielle kjennetegn",
          },
          idnr: {
            helpText:
              "For å kunne etterlyse fartøyet trenger vi et identifiserende nummer. Fyll derfor ut enten registreringsnummer eller skrognummer (WIN) dersom du ønsker å gjøre etterlysning mulig.",
            label: "Identifiserende nummer",
          },
          lengde: {
            label: "Lengde (fot)",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          nasjonalitet: {
            label: "Nasjonalitet",
          },
          pris: {
            helpText: "Oppgi utsalgspris eller omtrentlig pris i hele kroner.",
            label: "Pris (NOK)",
          },
          registreringsnummer: {
            helpText: "For eksempel serienummer, seilnummer",
            label: "Registreringsnummer",
          },
          skrogtype: {
            ENKELT_SKROG: "Enkelt skrog",
            KATAMARAN: "Katamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Annen type båt",
            GUMMIBAAT: "Gummibåt",
            JOLLE_ROBAAT: "Jolle/Robåt",
            KANO_KAJAKK: "Kano/Kajakk",
            MOTORBAAT: "Motorbåt",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Seilbåt",
            SKOEYTE: "Skøyte",
            SNEKKE: "Snekke",
            VANNSCOOTER: "Vannscooter",
          },
          typeFartoy: "Velg type fartøy",
          typeSkrog: "Skrogtype",
          win: {
            helpText: "Eventuelt CIN eller HIN",
            label: "WIN-kode",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 siffer)",
            label: "Årsmodell",
          },
          farge: {
            label: "Farge",
            options: {
              "01": "Hvit",
              "02": "Rød",
              "03": "Blå",
              "04": "Grønn",
              "05": "Grå",
              "06": "Svart",
              "07": "Beige",
              "08": "Brun",
              "09": "Gul",
              "10": "Oransje",
              "11": "Sølv",
              "12": "Gull",
              "13": "Rosa",
              "14": "Fiolett",
              "29": "Turkis",
              "99": "Annet",
            } satisfies Record<SVVFargekode, string>,
          },
          knapp: {
            manglerNorskRegNr: "Mangler norsk registreringsnummer",
            manglerRegNr: "Mangler registreringsnummer",
            norskRegNr: "Søk på norsk registreringsnummer",
            regnr: "Registreringsnummer",
            slett: "Slett",
            utenlandskRegNr: "Utenlandsk registreringsnummer",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          registreringsland: "Nasjonalitet",
          registreringsnummer: {
            feilmeldingSvv:
              "Fikk ikke kontakt med Statens vegvesen, prøv igjen senere",
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørrelsesglasset for å søke",
            labelUtenlandsk: "Utenlandsk registreringsnummer (kjennemerke)",
            panelLabel: "Registreringsnummer (kjennemerke)",
            ugyldig: "Registreringsnummeret må være mellom 2 og 7 tegn",
          },
          type: {
            ANNET: "Annet",
            BUSS: "Buss",
            BELTEBIL: "Beltebil",
            CAMPINGBIL: "Campingbil",
            CAMPINGVOGN: "Campingvogn",
            KOMBINERT_BIL: "Kombinert bil",
            LASTEBIL: "Lastebil",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorsykkel",
            PERSONBIL: "Personbil",
            SNOSCOOTER: "Snøscooter",
            TILHENGER: "Tilhenger",
            TRAKTOR: "Traktor",
            TREKKVOGN_ELLER_TREKKBIL: "Trekkvogn/-bil",
            VAREBIL: "Varebil",
          },
          typeKjoretoy: {
            label: "Velg type kjøretøy",
            seOverLabel: "Type kjøretøy",
          },
          understellsnummer: {
            label: "Understellsnummer (VIN)",
          },
        },
      },
      gjenstandTyper: {
        ANNET: "Annet",
        FARTOY: "Båt, vannscooter eller andre fartøy",
        HANDELSVARER: "Gjenstandsliste",
        KJENNEMERKE: "Registreringsskilt (kjennemerke)",
        KJOERETOEY: "Bil, moped eller andre kjøretøy",
        MOBILTELEFON: "Mobiltelefon",
        SYKKEL: "Sykkel eller sparkesykkel",
        ELEKTRONIKK: "Elektronikk",
        BYGNING_EIENDOM: "Bygning eller eiendom",
        VERKTOY: "Verktøy",
      },
      modal: {
        fullfoer: "Fullfør",
        lukk: "Lukk",
      },
      panel: {
        redigerAriaLabel: "Endre gjenstand {{nr}}",
        gjenstandTypeIkkeValg: "Gjenstand",
        slett: "Slett",
        ukjentGjenstandType: "Gjenstandtype mangler",
      },
    },
    bedrageri: {
      tittel: "Bedrageri",
      hvordanStartetBedrageriet: {
        title: "Hvordan startet det?",
        input: {
          initiellKontakt: {
            label:
              "Hvordan kom du først i kontakt med svindleren eller svindlerne?",
            annet: {
              label: "Beskriv hvordan du først kom i kontakt",
            },
            options: {
              MELDING_SMS: "Jeg fikk en melding eller sms",
              E_POST: "Jeg fikk en e-post",
              OPPRINGT: "Jeg ble oppringt",
              KJOEP_SALG: "På en tjeneste for kjøp og salg",
              SOSIALE_MEDIER: "På sosiale medier",
              ANNET: "Annet",
            },
          },
        },
      },
      hvaSkjedde: {
        title: "Hva skjedde?",
        options: {
          JA: "Ja",
          NEI: "Nei",
        } satisfies Record<BedrageriMetodeTypeSvar, string>,
        input: {
          beskrivelse: {
            label: "Beskriv hele hendelsen",
          },
          moettSvindlerFysisk: {
            label: "Møtte du svindleren eller svindlerne fysisk?",
          },
          harDuMottattLenke: {
            label: "Har du mottatt en lenke i forbindelse med svindelen?",
          },
          lenke: {
            label: "Lim inn lenken her",
          },
          harInstallertProgramEllerApp: {
            label:
              "Har du i forbindelse med hendelsen installert et program eller en app på en av dine enheter?",
          },
          hvilketProgramEllerApp: {
            label: "Hvilket program eller app ble installert?",
          },
          paaHvilkenEnhet: {
            label: "På hvilken enhet ble programmet eller appen installert?",
            options: {
              TELEFON: "Telefon",
              PC: "PC",
              NETTBRETT: "Nettbrett",
            },
          },
        },
      },
      tilegnedeVerdier: {
        title: "Hva har du mistet?",
        panel: {
          redigerAriaLabel: "Endre {{type}} {{nr}}",
        },
        options: {
          PENGER: "Penger",
          PERSONINFORMASJON: "Personinformasjon",
          KODE: "Kode",
          PASSORD: "Passord",
          FYSISK_ID: "Fysisk ID",
          ANNET: "Annet",
        } satisfies Record<TilegnetVerdiType, string>,
        input: {
          tilegnetVerdi: {
            label: "Hva har svindleren eller svindlerne fått tak i?",
            helpText: "Velg alle relevante alternativer",
          },
        },
        PENGER: {
          title: "Transaksjon",
          fjern: "Slett",
          modalTitle: "Beskrivelse av transaksjon",
          info: "Velg kortbetaling hvis du har brukt kort til å betale i falske nettbutikker eller andre falske nettsider eller hvis det er trekk på kontoen din med ukjent opprinnelse.",
          knapper: {
            leggTil: "Legg til transaksjon",
            leggTilFlere: "Legg til flere transaksjoner",
          },
          fratattSum: {
            label: "Hvor mye fikk svindleren eller svindlerne tak i?",
            seOverLabel: "Sum",
            helpText: "Oppgi summen i hele NOK",
            totalSum: "Totalsum stjålne penger (NOK)",
          },
          overforing: {
            dropdown: {
              label: "Hvordan fikk svindleren eller svindlerne tak i pengene?",
            },
            options: {
              KONTOOVERFORING: "Kontooverføring",
              VIPPS: "Vipps-overføring",
              KRYPTO: "Kryptotransaksjon",
              KORTBETALING: "Kortbetaling",
              ANNET: "Annet",
            } satisfies Record<OverforingType, string>,
            KONTOOVERFORING: {
              fraKontonummer: {
                label: "Fra hvilken konto ble pengene overført?",
                seOverLabel: "Fra konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              tilKontonummer: {
                label: "Til hvilken konto ble pengene overført? (valgfritt)",
                seOverLabel: "Til konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              fraBank: {
                label: "I hvilken bank er denne kontoen?",
                seOverLabel: "Bank",
              },
            },
            KORTBETALING: {
              kortnummer: {
                infoboks:
                  "Kortnummer og kontonummer er viktig informasjon hvis vi skal etterforske hendelsen. Det er derfor viktig at du oppgir disse opplysningene hvis du har dem.",
                label: "Hvilket kort ble brukt i betalingen? (valgfritt)",
                seOverLabel: "Kortnummer",
                helpText: "Oppgi kortnummer",
              },
              fraKontonummer: {
                label: "Fra hvilken konto ble pengene overført? (valgfritt)",
                seOverLabel: "Fra konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              fraBank: {
                label: "I hvilken bank er denne kontoen?",
                seOverLabel: "Bank",
              },
              mottaker: {
                label: "Til hvilken mottaker ble pengene betalt?",
                seOverLabel: "Til mottaker",
                helpText: "For eksempel navn på person eller virksomhet",
              },
            },
            VIPPSOVERFORING: {
              fraKontonummer: {
                label: "Fra hvilken konto ble pengene overført? (valgfritt)",
                seOverLabel: "Fra konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              fraTelefonnummer: {
                label: "Fra hvilket telefonnummer ble pengene overført?",
                seOverLabel: "Fra telefonnummer",
              },
              tilTelefonnummer: {
                label:
                  "Til hvilket telefonnummer eller Vippsnummer ble pengene overført?",
                seOverLabel: "Til telefonnummer / Vippsnummer",
              },
              fraBank: {
                label: "I hvilken bank er denne kontoen?",
                seOverLabel: "Bank",
              },
              mottakernavn: {
                label: "Navn på mottaker",
                seOverLabel: "Mottaker",
              },
            },
            KRYPTO: {
              belop: {
                label: "Hva var beløpet i kryptovaluta?",
                helpText: "Oppgi beløpet i gjeldende kryptovaluta",
              },
              valuta: {
                label: "Oppgi kryptovaluta",
                helpText: "For eksempel Bitcoin, Ethereum, XRP",
              },
              fraAdresse: {
                label: "Fra hvilken kryptovalutaadresse ble pengene tatt?",
                seOverLabel: "Fra kryptovalutaadresse",
                helpText: "Fra kryptovaluta-adresse",
              },
              tilAdresse: {
                label:
                  "Hvilken kryptovalutaadresse gikk pengene til? (valgfritt)",
                seOverLabel: "Til kryptovaluta-adresse",
              },
              vekslingstjeneste: {
                label: "Hvilken vekslingstjeneste ble benyttet?",
                helpText: "Oppgi kryptovalutabørsen som ble brukt",
                seOverLabel: "Vekslingstjeneste",
              },
              transaksjonsId: {
                label: "Transaksjons-ID (hash) på overføringen",
                seOverLabel: "Transaksjons-ID",
              },
            },
            ANNET: {
              beskrivelse: {
                label: "Beskriv hvordan pengene ble tatt og hvor de gikk",
                seOverLabel: "Beskrivelse",
                helpText:
                  "For eksempel uttak i navngitt minibank eller bruk i navngitt butikk",
              },
            },
          },
          selvOverfort: {
            harDuSelvOverfortPengene: {
              options: {
                JA: "Ja",
                NEI: "Nei",
              } satisfies Record<BedrageriMetodeTypeSvar, string>,
              label:
                "Har du selv overført pengene til svindleren eller svindlerne?",
            },
            overforingFormaal: {
              label: "Hva var formålet med overføringen?",
              options: {
                INVESTERING: "Investering",
                VARER_TJENESTER: "Kjøp av varer eller tjenester",
                GAVE: "Gave",
                UTLAAN: "Utlån",
                FAKTURA: "Faktura (falsk)",
                UTPRESSING: "Utpressing",
                ABONNEMENT: "Abonnement",
                GEBYR: "Gebyr",
                ANNET: "Annet",
              } satisfies Record<OverforingFormaalType, string>,
            },
            annetOverforingFormaalBeskrivelse: {
              label: "Forklar hva formålet med overføringen var",
            },
            erOverforingGodkjentMedPassordEtc: {
              label:
                "Har du godkjent overføringer ved å taste kode eller passord?",
              options: {
                JA: "Ja",
                NEI: "Nei",
              } satisfies Record<BedrageriMetodeTypeSvar, string>,
            },
          },
          tilbakeforing: {
            type: {
              label: "Har du fått pengene tilbake?",
              options: {
                JA: "Ja",
                NEI: "Nei",
                DELVIS: "Delvis",
              } satisfies Record<TilbakeforingType, string>,
            },
            hvemHarFornaermedeFaattPengerFra: {
              label: "Hvem har du fått pengene tilbake fra?",
            },
            hvorMyeHarFornaermedeFaattTilbake: {
              label: "Hvor mye har du fått tilbake?",
              helpText: "Oppgi beløpet i NOK",
            },
          },
          bankenStansetOverforing: {
            overforingStanset: {
              label: "Har banken stanset pengene?",
            },
            hvorMyeHarBankenStanset: {
              label: "Hvor mye har banken stanset?",
              helpText: "Oppgi beløpet i NOK",
            },
            optionsJaNeiDelvis: {
              JA: "Ja",
              NEI: "Nei",
              DELVIS: "Delvis",
            } satisfies Record<HarBankenStansetOverforingType, string>,
          },
        },
        PERSONINFORMASJON: {
          checkbox: {
            label:
              "Hvilke personopplysninger har svindleren eller svindlerne fått tak i?",
            options: {
              NAVN: "Navn",
              ADRESSE: "Adresse",
              TELEFONNUMMER: "Telefonnummer",
              FOEDSELSNUMMER_ELLER_DNUMMER: "Fødselsnummer / D-nummer",
              ANNET: "Annen personinformasjon",
            } satisfies Record<TilegnetPersoninformasjonVerdiType, string>,
            extraInput: {
              ANNET: {
                label: "Forklar hvilke personopplysninger",
              },
            },
          },
        },
        KODE: {
          beskrivelse: {
            label: "Hva var koden til?",
          },
        },
        PASSORD: {
          beskrivelse: {
            label: "Hva var passordet til?",
          },
        },
        FYSISK_ID: {
          checkbox: {
            label:
              "Hva slags fysisk ID har svindleren eller svindlerne fått tak i?",
            options: {
              PASS: "Pass",
              NASJONALT_ID_KORT: "Nasjonalt ID-kort",
              FORERKORT: "Førerkort",
              BANKKORT_MED_BILDE: "Bankkort med bilde",
              ANNET: "Annet",
            } satisfies Record<TilegnetVerdiFysiskIdType, string>,
            extraInput: {
              ANNET: {
                label: "Forklar hva slags fysisk ID",
              },
            },
          },
        },
        ANNET: {
          beskrivelse: {
            label: "Beskriv det svindlerne har tatt",
            helpText: "Hva er det? Er det levert tilbake? Antall?",
          },
          sum: {
            label: "Verdi av det som er tatt (NOK)",
            helpText: "Oppgi utsalgspris eller omtrentlig pris i hele kroner.",
          },
        },
      },
      hvaSkjeddeEtter: {
        title: "Hva skjedde etterpå?",
        optionsJaNei: {
          JA: "Ja",
          NEI: "Nei",
        } satisfies Record<EgneTiltakType, string>,
        input: {
          vetBanken: {
            label: "Vet banken din hva som har skjedd?",
          },
          kortSperret: {
            label: "Er kortet ditt sperret?",
          },
          kontoSperret: {
            label: "Er kontoen din sperret?",
          },
        },
      },
    },
    innbrudd: {
      beskrivelse: {
        helpText: "For eksempel bod, bil, lager",
        label: "Hva var det innbrudd i?",
      },
      se_over: {
        beskrivelse: "Beskrivelse",
        label: "Hva var det innbrudd i?",
      },
      tittel: "Innbrudd",
    },
    options: {
      INNBRUDD: { label: "Noen har brutt seg inn et sted (innbrudd)" },
      SKADEVERK: { label: "Noen har ødelagt noe (skadeverk)" },
      TYVERI: { label: "Noen har stjålet noe (tyveri)" },
      BEDRAGERI: {
        label: "Bedrageri (svindel)",
        helpText:
          "Noen har lurt eller forsøkt å lure til seg penger eller verdier de ikke har rett til",
      },
      DATAKRIM: {
        label: "Datakriminalitet",
        helpText:
          "Noen har ulovlig tatt seg inn i et datasystem og stjålet, endret eller ødelagt data",
      },
    } satisfies Record<HandlingType, { label: string; helpText?: string }>,
    datakrim: {
      tittel: "Datakriminalitet",
      infoboks_sikkert_dot_no:
        "På <anchorlink>sikkert.no</anchorlink> får du flere råd fra myndighetene om å håndtere en uønsket digital hendelse, eller forhindre at det skjer i fremtiden.",
      datakrimType: {
        checkbox: {
          label: "Hva beskriver best det som har skjedd?",
          helpText: "Kryss av for det som passer (flere valg mulig)",
          extraInput: {
            ANNET: {
              label: "Beskriv kort hva som har skjedd",
              se_over: "Beskriv kort hva som har skjedd (annet)",
            },
          },
          options: {
            DATAINNBRUDD: {
              label: "Datainnbrudd",
              helpText:
                "noen har skaffet seg urettmessig tilgang til et datasystem",
            },
            VIRUS: {
              label: "Løspengevirus eller skadevare",
              helpText:
                "programvare som har låst eller kryptert innhold i et datasystem",
            },
            DOS_DDOS: {
              label: "Tjenestenektangrep (DoS)",
              helpText:
                "angrep som gjør informasjon, ressurser eller tjenester helt eller delvis utilgjengelige",
            },
            KJOP_SALG_INNLOGGINGSINFORMASJON: {
              label: "Kjøp og salg av påloggingsinformasjon",
            },
            ID_TYVERI: { label: "ID-tyveri eller kontokapring" },
            ANNET: { label: "Annet" },
          } satisfies Record<
            DatakrimType,
            { label: string; helpText?: string }
          >,
        },
      },
      dataPaAvveie: {
        label: "Er det grunn til å tro at data er på avveie?",
        beskrivelse: {
          label: "Forklar hvorfor",
        },
      },
      andreMyndigheterVarslet: {
        label:
          "Er andre myndigheter eller virksomheter blitt varslet eller involvert i håndteringen av denne hendelsen?",
        beskrivelse: {
          label: "Hvilke myndigheter eller virksomheter er involvert?",
        },
      },
      datakrimSvarType: {
        JA: "Ja",
        NEI: "Nei",
      } satisfies Record<DatakrimSvarType, string>,
      tilleggsinformasjon: {
        label: "Informasjon om hendelsen eller de som står bak (valgfritt)",
        helpText:
          "For eksempel IP-adresser, domener, hasher, filnavn, e-postadresser, kryptovaluttaadresser eller navn",
      },
    },
    skadeverk: {
      beskrivelse: {
        helpText: "For eksempel hva som er skadet eller hvordan det har skjedd",
        label: "Beskriv skadeverket",
      },
      formuestap: {
        helpText: "Hvis du er usikker, kan du oppgi omtrentlig pris",
        label: "Pris på det som er ødelagt (NOK)",
      },
      gjenstander: {
        bygningEiendom: {
          beskrivelse: {
            helpText:
              "Hvilken del av av eiendommen er det? Hvordan ble det ødelagt?",
            label: "Beskriv det som er ødelagt",
          },
          panel: {
            beskrivelse: {
              label: "Beskrivelse",
            },
            pris: "Pris",
          },
        },
        annenGjenstand: {
          beskrivelse: {
            helpText: "Hva er det? Hvordan ble det ødelagt?",
            label: "Beskriv det som er ødelagt",
          },
          panel: {
            beskrivelse: {
              label: "Beskrivelse",
            },
            pris: "Pris",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
            label: "Spesielle kjennetegn",
          },
          idnr: {
            helpText:
              "For å kunne etterlyse fartøyet trenger vi et identifiserende nummer. Fyll derfor ut enten registreringsnummer eller skrognummer (WIN) dersom du ønsker å gjøre etterlysning mulig.",
            label: "Identifiserende nummer",
          },
          lengde: {
            label: "Lengde (fot)",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          nasjonalitet: {
            label: "Nasjonalitet",
          },
          pris: {
            helpText: "Oppgi utsalgspris eller omtrentlig pris i hele kroner.",
            label: "Pris (NOK)",
          },
          registreringsnummer: {
            helpText: "For eksempel serienummer, seilnummer",
            label: "Registreringsnummer",
          },
          skrogtype: {
            ENKELT_SKROG: "Enkelt skrog",
            KATAMARAN: "Katamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Annen type båt",
            GUMMIBAAT: "Gummibåt",
            JOLLE_ROBAAT: "Jolle/Robåt",
            KANO_KAJAKK: "Kano/Kajakk",
            MOTORBAAT: "Motorbåt",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Seilbåt",
            SKOEYTE: "Skøyte",
            SNEKKE: "Snekke",
            VANNSCOOTER: "Vannscooter",
          },
          typeFartoy: "Type fartøy",
          typeLabel: "Type fartøy",
          typeSkrog: "Skrogtype",
          win: {
            helpText: "Eventuelt CIN eller HIN",
            label: "WIN-kode",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 siffer)",
            label: "Årsmodell",
          },
          farge: {
            label: "Farge",
          },
          knapp: {
            manglerNorskRegNr: "Mangler norsk registreringsnummer",
            manglerRegNr: "Mangler registreringsnummer",
            norskRegNr: "Søk på norsk registreringsnummer",
            slett: "Slett",
            utenlandskRegNr: "Utenlandsk registreringsnummer",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          registreringsland: "Nasjonalitet",
          registreringsnummer: {
            feilmeldingSvv:
              "Fikk ikke kontakt med Statens vegvesen, prøv igjen senere",
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørrelsesglasset for å søke",
            labelUtenlandsk: "Utenlandsk registreringsnummer (kjennemerke)",
            panelLabel: "Registreringsnummer (kjennemerke)",
            ugyldig: "Registreringsnummeret må være mellom 2 og 7 tegn",
          },
          type: {
            ANNET: "Annet",
            BUSS: "Buss",
            BELTEBIL: "Beltebil",
            CAMPINGBIL: "Campingbil",
            CAMPINGVOGN: "Campingvogn",
            KOMBINERT_BIL: "Kombinert bil",
            LASTEBIL: "Lastebil",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorsykkel",
            PERSONBIL: "Personbil",
            SNOSCOOTER: "Snøscooter",
            TILHENGER: "Tilhenger",
            TRAKTOR: "Traktor",
            TREKKVOGN_ELLER_TREKKBIL: "Trekkvogn/-bil",
            VAREBIL: "Varebil",
          },
          typeKjoretoy: {
            label: "Velg type kjøretøy",
          },
          understellsnummer: {
            label: "Understellsnummer (VIN)",
          },
        },
        sum: "Sum ødelagte gjenstander (NOK)",
      },
      leggTilFlereGjenstander: "Legg til flere ødelagte gjenstander",
      leggTilGjenstand: "Legg til ødelagt gjenstand",
      modal: {
        dropdownLabel: "Hva er ødelagt?",
        tittel: "Beskrivelse av ødelagt gjenstand",
      },
      panel: {
        beskrivelse: {
          label: "Skadebeskrivelse",
        },
        dropdownLabel: "Hva er ødelagt?",
        formuestap: {
          label: "Pris",
        },
      },
      tittel: "Skadeverk",
    },
    tyveri: {
      beskrivelse: {
        helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
        label: "Spesielle kjennetegn",
      },
      gjenstander: {
        annenGjenstand: {
          beskrivelse: {
            helpText: "Hva er det? Er det levert tilbake? Antall?",
            label: "Beskriv det som er stjålet",
          },
          panel: {
            beskrivelse: {
              label: "Beskrivelse",
            },
            pris: "Pris",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
            label: "Spesielle kjennetegn",
          },
          idnr: {
            helpText:
              "For å kunne etterlyse fartøyet trenger vi et identifiserende nummer. Fyll derfor ut enten registreringsnummer eller skrognummer (WIN) dersom du ønsker å gjøre etterlysning mulig.",
            label: "Identifiserende nummer",
          },
          lengde: {
            label: "Lengde (fot)",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          nasjonalitet: {
            label: "Nasjonalitet",
          },
          pris: {
            helpText: "Oppgi utsalgspris eller omtrentlig pris i hele kroner.",
            label: "Pris (NOK)",
          },
          registreringsnummer: {
            helpText: "For eksempel serienummer, seilnummer",
            label: "Registreringsnummer",
          },
          skrogtype: {
            ENKELT_SKROG: "Enkelt skrog",
            KATAMARAN: "Katamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Annen type båt",
            GUMMIBAAT: "Gummibåt",
            JOLLE_ROBAAT: "Jolle/Robåt",
            KANO_KAJAKK: "Kano/Kajakk",
            MOTORBAAT: "Motorbåt",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Seilbåt",
            SKOEYTE: "Skøyte",
            SNEKKE: "Snekke",
            VANNSCOOTER: "Vannscooter",
          },
          typeFartoy: "Type fartøy",
          typeLabel: "Type fartøy",
          typeSkrog: "Skrogtype",
          win: {
            helpText: "Eventuelt CIN eller HIN",
            label: "WIN-kode",
          },
        },
        handelsvarer: {
          caption: {
            label: "Gjenstandsliste",
          },
          antall: {
            label: "Antall",
          },
          leggTilFlereVarer: "Legg til flere gjenstander",
          leggTilVare: "Legg til gjenstand",
          slett: {
            ariaLabel: "Slett gjenstand",
          },
          navn: {
            label: "Gjenstandstype og merke",
          },
          pris: {
            label: "Pris per stykk (NOK)",
          },
          total: {
            label: "Sum gjenstander (NOK)",
          },
        },
        kjennemerke: {
          kategori: {
            label: "Kjennemerkekategori",
            options: {
              KJORETOY: "Hovedkjennemerke",
              NORMAL: "Normal",
              PERSONLIG: "Personlig kjennemerke",
              PROVE: "Prøvekjennemerke",
            } satisfies Record<KjennemerkeKategori, string>,
          },
          type: {
            label: "Kjennemerketype",
            options: {
              AMBASSADE: "Ambassadekjennemerker (Gule tegn på blå bunn)",
              ARSPROVEKJENNEMERKE:
                "Årsprøvekjennemerke (Hvite tegn på rød bunn)",
              ARSPROVEKJENNEMERKE_T:
                "Årsprøvekjennemerke Tilhenger (Hvite tegn på rød bunn med T)",
              DAGSPROVEKJENNEMERKE:
                "Dagsprøvekjennemerke (Sorte tegn på rød bunn)",
              ORDINART: "Ordinære kjennemerker (Sorte tegn på hvit bunn)",
              RALLY_TRIAL_ENDURO: "Rallykjennemerker (Hvite tegn på sort bunn)",
              SVALBARD: "Svalbard-kjennemerker (Gule tegn på sort bunn)",
              UTENFOR_OFFENTLIG_VEG:
                "Utenfor offentlig vei (Gule tegn på sort bunn)",
              VAREBIL_KLASSE_2: "Varebil klasse 2 (Sorte tegn på grønn bunn)",
              VETERAN: "Veterankjennemerker",
              VETERAN_UTENFOR_OFFENTLIG_VEI:
                "Veteran utenfor offentlig veg (Gule tegn på sort bunn)",
            } satisfies Record<KjennemerketypeType, string>,
          },
          antallSkilt: {
            label: "Hvor mange skilt er stjålet?",
          },
          panel: {
            antallSkilt: "Antall stjålne skilt",
            registreringsnummer: "Registreringsnummer (kjennemerke)",
          },
          registreringsnummer: {
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørrelsesglasset for å søke",
          },
          alert: {
            header: "Er registreringsskiltet mistet?",
            body: "Da skal det ikke anmeldes som stjålet. I stedet må du fylle ut  <0>skjema om tap av kjennemerke</0>. Ferdig utfylt skjema må signeres og stemples på en politistasjon",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 siffer)",
            label: "Årsmodell",
          },
          farge: {
            label: "Farge",
          },
          knapp: {
            manglerNorskRegNr: "Mangler norsk registreringsnummer",
            manglerRegNr: "Mangler registreringsnummer",
            norskRegNr: "Søk på norsk registreringsnummer",
            slett: "Slett",
            utenlandskRegNr: "Utenlandsk registreringsnummer",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          registreringsland: "Nasjonalitet",
          registreringsnummer: {
            feilmeldingSvv:
              "Fikk ikke kontakt med Statens vegvesen, prøv igjen senere",
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørrelsesglasset for å søke",
            labelUtenlandsk: "Utenlandsk registreringsnummer (kjennemerke)",
            panelLabel: "Registreringsnummer (kjennemerke)",
            ugyldig: "Registreringsnummeret må være mellom 2 og 7 tegn",
          },
          type: {
            ANNET: "Annet",
            BUSS: "Buss",
            BELTEBIL: "Beltebil",
            CAMPINGBIL: "Campingbil",
            CAMPINGVOGN: "Campingvogn",
            KOMBINERT_BIL: "Kombinert bil",
            LASTEBIL: "Lastebil",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorsykkel",
            PERSONBIL: "Personbil",
            SNOSCOOTER: "Snøscooter",
            TILHENGER: "Tilhenger",
            TRAKTOR: "Traktor",
            TREKKVOGN_ELLER_TREKKBIL: "Trekkvogn/-bil",
            VAREBIL: "Varebil",
          },
          typeKjoretoy: {
            label: "Type kjøretøy",
          },
          understellsnummer: {
            label: "Understellsnummer (VIN)",
          },
        },
        mobiltelefon: {
          imei: {
            label: "IMEI-nummer",
            helpText: "15 siffer",
          },
          merke: {
            helpText: "For eksempel Apple, Samsung",
            label: "Merke",
          },
          modell: {
            helpText: "For eksempel iPhone 14 Pro, Galaxy A25",
            label: "Modell",
          },
          pris: {
            label: "Pris",
          },
          telefonnummer: {
            label: "Telefonnummer",
          },
          teleselskap: {
            helpText: "For eksempel Telenor, Telia",
            label: "Teleselskap",
          },
        },
        sykkel: {
          merke: {
            helpText: "For eksempel DBS, Merida, Scott",
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          rammenummer: {
            helpText: "Oppgi rammenummer hvis du har det",
            label: "Rammenummer/serienummer",
          },
          type: {
            ANNEN_TYPE_SYKKEL: "Annen type sykkel",
            BARNESYKKEL: "Barnesykkel",
            BYSYKKEL: "Bysykkel",
            CYCLOCROSS_GRAVEL: "Cyclocross / Gravel",
            EL_SPARKESYKKEL: "Elsparkesykkel",
            EL_SYKKEL: "Elsykkel",
            HYBRIDSYKKEL: "Hybridsykkel",
            LANDEVEISSYKKEL: "Landeveissykkel",
            SPARKESYKKEL: "Sparkesykkel",
            TERRENGSYKKEL: "Terrengsykkel",
          },
          typeSykkel: "Velg type sykkel",
        },
        elektronikk: {
          type: {
            NETTBRETT: "Nettbrett",
            DATAMASKIN: "Datamaskin",
            SPILLKONSOLL: "Spillkonsoll",
            SMARTKLOKKE: "Smartklokke",
            TV: "TV",
            HODETELEFONER: "Hodetelefoner",
            HVITEVARER: "Hvitevarer",
            STØVSUGER: "Støvsuger",
            ANNET: "Annet",
          } satisfies Record<ElektronikkType, string>,
          typeElektronikk: "Velg type elektronikk",
          merke: {
            label: "Merke",
            helpText: "For eksempel Samsung, Playstation, Apple,",
          },
          modell: {
            label: "Modell",
            helpText: "For eksempel Galaxy buds, PS5, MacBook Pro",
          },
          serienummer: {
            label: "Serienummer (valgfritt)",
          },
          pris: {
            label: "Pris",
          },
        },
        verktoy: {
          type: {
            label: "Type verktøy",
            helpText: "For eksempel drill, sag, vinkelsliper",
          },
          merke: {
            label: "Merke",
            helpText: "For eksempel Bosch, Ryobi, DeWalt",
          },
          modell: {
            label: "Modell",
          },
          serienummer: {
            label: "Serienummer (valgfritt)",
          },
        },
        sum: "Totalpris stjålne gjenstander (NOK)",
      },
      leggTilFlereGjenstander: "Legg til flere stjålne gjenstander",
      leggTilGjenstand: "Legg til stjålet gjenstand",
      modal: {
        dropdownLabel: "Hva er stjålet?",
        tittel: "Beskrivelse av stjålet gjenstand",
      },
      panel: {
        beskrivelse: {
          label: "Spesielle kjennetegn",
        },
        dropdownLabel: "Hva er stjålet?",
        pris: {
          label: "Pris",
        },
      },
      pris: {
        helpText: "Oppgi utsalgspris eller omtrentlig pris i hele kroner.",
        label: "Pris på det som er stjålet (NOK)",
      },
      tittel: "Tyveri",
    },
  },
  hvemGjordeDet: {
    navbar: "Gjerningsperson stegvelger",
    gjerningspersoninformasjonSvarType: {
      KJENT: "Ja",
      UKJENT: "Nei, jeg vet ikke hvem det er",
    },
    modal: {
      tittel: "Om gjerningspersonen",
      forrige: "Forrige",
      neste: "Neste",
      fullfoer: "Fullfør",
      lukk: "Lukk",
      avbryt: "Avbryt",
      fornavn: "Oppgitt fornavn",
      etternavn: "Oppgitt etternavn",
      epost: "E-postadresse",
      telefon: "Telefonnummer",
      beskrivelse: "Andre opplysninger",
    },
    panel: {
      antattGjerningsperson: "Antatt gjerningsperson",
      gjerningspersonNr: "Gjerningsperson {{nr}}",
      slett: "Slett gjerningsperson",
      redigerAriaLabel: "Endre gjerningsperson {{nr}}",
    },
    gjerningsperson: {
      details: {
        gjerningspersonNr: "Gjerningsperson {{nr}}",
        label: "Legg til én eller flere typer opplysninger",
        alder: {
          gjerningspersonAlderSvarType: {
            OVER_ATTEN: "Over 18 år",
            UNDER_ATTEN: "Under 18 år",
            UNDER_FEMTEN: "Under 15 år",
            VET_IKKE: "Vet ikke",
          },
          label: "Hvor gammel er gjerningspersonen?",
          title: "Alder",
          vergeInfo:
            "Personen du har lagt til er under 18 år. Har du opplysninger om verge eller foresatt? Da kan du legge dem til med egen knapp.",
        },
        begrunnelseForInvolvering: {
          label: "Hvorfor mener du at denne personen har gjort det?",
        },
        slett: "Slett gjerningsperson",
      },
      panel: {
        endre: {
          label: "Endre",
        },
        slett: {
          label: "Slett",
        },
      },
      fragments: {
        personopplysninger: {
          leggTil: "Legg til navn eller andre personopplysninger",
          tittel: "Personopplysninger",
          helptext:
            "Du må oppgi <strong>minst én</strong> personopplysning, men gjerne så mange du kan",
          fornavn: "Fornavn",
          etternavn: "Etternavn",
          foedselsEllerDnummer: {
            label: "Personlig ID / 11 siffer",
            helpText: "Fødselsnummer, D-nummer",
          },
          foedselsdato: {
            label: "Fødselsdato",
            helpText: "dd.mm.åååå",
          },
          identifiseringsinformasjon: {
            beskrivelse: {
              ANNET: {
                label: "Beskriv hvordan du har identifisert personen",
              },
              FREMVIST_ID: {
                label: "Hva slags ID er fremvist?",
              },
            },
            label: "Hvordan er personen identifisert?",
            options: {
              ANNET: "Annet",
              FREMVIST_ID: "Fremvist ID",
              IKKE_IDENTIFISERT: "Personen er ikke identifisert",
            },
          },
          vergeInfo:
            "Personen du har lagt til er under 18 år. Har du opplysninger om verge eller foresatt? Da kan du legge dem til med egen knapp.",
        },
        beskrivelse: {
          leggTil: "Legg til en beskrivelse av personen",
          tittel: "Beskrivelse",
          label: "Beskriv gjerningspersonen så godt du kan",
          helptext:
            "For eksempel kjønn, hårfarge og -lengde, antatt alder, høyde, klær, språk, arr osv.",
        },
        verge: {
          leggTil: "Legg til opplysninger om foresatt eller verge",
          tittel: "Opplysninger om foresatt eller verge",
          personnavn: {
            etternavn: {
              label: "Etternavn",
            },
            fornavn: {
              label: "Fornavn",
            },
          },
          kontaktopplysninger: {
            telefon: {
              label: "Telefonnummer",
            },
          },
          vergeKontaktet: {
            tittel: "Har verge blitt kontaktet?",
            vergeErKontaktetSvarType: {
              JA: "Ja",
              NEI: "Nei",
            },
          },
          vergeTilStede: {
            tittel: "Kom verge til stedet?",
            vergeTilStedeSvarType: {
              JA: "Ja",
              NEI: "Nei",
            },
          },
          panel: {
            personnavn: {
              fornavn: "Verges fornavn",
              etternavn: "Verges etternavn",
            },
            kontaktopplysninger: {
              telefon: "Verges telefonnummer",
            },
          },
        },
        oppgitteOpplysninger: {
          leggTil: "Legg til oppgitt navn og/eller organisasjon",
          tittel: "Oppgitt navn og/eller organisasjon",
          helptext:
            "Du må fylle ut <strong>minst ett</strong> felt, men gjerne så mange du kan",
          personnavn: {
            fornavn: "Hvilket fornavn oppga personen?",
            etternavn: "Hvilket etternavn oppga personen?",
          },
          virksomhetNavn:
            "Hvilken organisasjon eller virksomhet sa personen at den representerte?",
          andreOpplysninger: "Andre opplysninger om oppgitt identitet",
          panel: {
            personnavn: {
              fornavn: "Oppgitt fornavn",
              etternavn: "Oppgitt etternavn",
            },
            virksomhetNavn: "Organisasjon",
            andreOpplysninger: "Andre opplysninger",
          },
        },
        kontaktopplysninger: {
          leggTil: "Legg til kontaktopplysninger",
          tittel: "Kontaktopplysninger",
          slett: "Slett",
          label: "Hvilke kontaktopplysninger har du?",
          helptext: "Kryss av for ett eller flere alternativ som passer",
          options: {
            TELEFONNUMMER: {
              label: "Telefonnummer",
              leggTil: "Legg til telefonnummer",
            },
            EPOST: {
              label: "E-postadresse",
              leggTil: "Legg til e-postadresse",
            },
            BRUKERNAVN: {
              label: "Brukernavn",
              leggTil: "Legg til brukernavn",
              context: "Hvor ble det brukt?",
              contextHelptext: "For eksempel Finn.no, Whatsapp",
            },
            ADRESSE: {
              label: "Adresse",
              leggTil: "Legg til adresse",
            },
            ANNET: {
              label: "Annet",
              leggTil: "Legg til annet",
            },
          } satisfies Record<
            ExperimentalTypeKontaktopplysning,
            { label: string; leggTil: string; [key: string]: string }
          >,
          panel: {
            brukernavn: {
              context: "Hvor ble brukernavnet brukt?",
            },
          },
        },
      } satisfies GjerningspersonFragmentRequiredTexts,
    },
    gjerningsvirksomhet: {
      panel: {
        tittel: "Virksomhet",
        navnMangler: "(navn mangler)",
      },
      sokefelt: {
        label: "Søk i virksomhetsregisteret",
        helpText: "Søk på navn eller organisasjonsnummer",
        feilmeldingBrreg:
          "Fikk ikke kontakt med Enhetsregisteret, prøv igjen senere",
      },
      leggTilManuelt: "Legg til virksomhet manuelt",
      valgtVirksomhet: {
        opplysninger: "Opplysninger fra virksomhetsregisteret",
        manuelleOpplysninger: "Opplysninger lagt til manuelt",
        endre: "Endre",
      },
      manuellVirksomhet: {
        modalTittel: "Legg til virksomhet manuelt",
        virksomhetensNavn: {
          label: "Virksomhetens navn",
        },
        informasjon: {
          label: "Hvilke opplysninger har du om virksomheten? (valgfritt)",
          helpText: "Kryss av for ett eller flere alternativ som passer",
        },
        informasjonstyper: {
          NETTSTED: "Nettsted",
          E_POST: "E-post",
          TELEFON: "Telefonnummer",
          ADRESSE: "Adresse",
          ORGNR: "Organisasjonsnummer",
          ANNET: "Annet",
        },
        input: {
          NETTSTED: "Nettsted",
          E_POST: "E-postadresse",
          TELEFON: "Telefonnummer",
          ADRESSE: "Adresse",
          ORGNR: "Organisasjonsnummer",
          ANNET: "Andre opplysninger",
        },
        fullfoer: "Fullfør",
        avbryt: "Avbryt",
      },
      hvorforSkyldig: {
        label: "Hvorfor mener du at denne virksomheten har gjort det?",
      },
      slett: "Slett virksomhet",
    },
    radioKnapper: {
      fritekst: "Ja, jeg kan gi en beskrivelse",
      kjent: "Ja",
      tittel: "Har du opplysninger om den eller de du mener har gjort det?",
      helpText: "Du kan legge til flere gjerningspersoner",
      ukjent: "Nei, jeg vet ikke hvem det er",
    },
    steg: {
      alder: {
        gjerningspersonAlderSvarType: {
          OVER_ATTEN: "Over 18 år",
          UNDER_ATTEN: "Under 18 år",
          UNDER_FEMTEN: "Under 15 år",
          VET_IKKE: "Vet ikke",
        },
        label: "Hvor gammel er gjerningspersonen?",
        title: "Alder",
      },
      begrunnelse: {
        begrunnelseForInvolvering: {
          label: "Hvorfor mener du at denne personen har gjort det?",
        },
        identifiseringsinformasjon: {
          beskrivelse: {
            ANNET: {
              label: "Beskriv hvordan du har identifisert personen",
            },
            FREMVIST_ID: {
              label: "Hva slags ID er fremvist?",
            },
          },
          label: "Hvordan er personen identifisert?",
          options: {
            ANNET: "Annet",
            FREMVIST_ID: "Fremvist ID",
            IKKE_IDENTIFISERT: "Personen er ikke identifisert",
          },
        },
        title: "Begrunnelse",
      },
      personalia: {
        beskrivelse: "Andre opplysninger om gjerningspersonen",
        beskrivelseHelpText:
          "For eksempel kjønn, hårfarge, alder, høyde, fasong, klær, språk, dialekt, arr osv.",
        beskrivelseHelpTextBedrageri:
          "For eksempel flere brukernavn, språk/dialekt, oppgitt tilhørighet/organisasjon",
        title: "Personalia",
      },
      verge: {
        harKontaktinformasjonTilVerge: {
          title: "Har du kontaktinformasjon til verge?",
        },
        harVergesKontaktinformasjonSvarType: {
          JA: "Ja",
          NEI: "Nei",
        },
        kontaktopplysninger: {
          telefon: {
            label: "Telefonnummer",
          },
        },
        label: "Kontaktopplysninger til verge",
        personnavn: {
          etternavn: {
            label: "Etternavn",
          },
          fornavn: {
            label: "Fornavn",
          },
        },
        title: "Verge",
        vergeKontaktet: {
          tittel: "Har du kontaktet vergen?",
          vergeErKontaktetSvarType: {
            JA: "Ja",
            NEI: "Nei",
          },
        },
        vergeTilStede: {
          tittel: "Kom vergen til stedet?",
          vergeTilStedeSvarType: {
            JA: "Ja",
            NEI: "Nei",
          },
        },
      },
    },
    input: {
      beskrivelse: "Andre opplysninger om gjerningspersonen",
      beskrivelseHelpText:
        "For eksempel kjønn, hårfarge, alder, høyde, fasong, klær, språk, dialekt, arr osv.",
      fornavn: "Fornavn",
      etternavn: "Etternavn",
      telefon: "Telefonnummer",
      vergeLabel: "Kontaktopplysninger til verge",
    },
    leggTilGjerningsvirksomhet: "Legg til virksomhet",
    leggTilFlereGjerningsperson: "Legg til flere gjerningspersoner",
    leggTilGjerningsperson: "Legg til gjerningsperson",
  },
  hvemSaaDet: {
    anmelderErVitne: "Du blir registrert som et vitne i saken",
    infoboks:
      "Vitner er viktige for å belyse hva som har skjedd. I tilfelle vi må hente inn mer informasjon, så trenger vi kontaktinformasjonen til eventuelle vitner.",
    input: {
      beskrivelse: "Andre opplysninger om vitnet",
      beskrivelseHelpText:
        "For eksempel rolle, ansattforhold, hva vitnet har observert",
    },
    ja: "Ja",
    leggTilFlereVitner: "Legg til flere vitner",
    leggTilVitne: "Legg til vitne",
    modal: {
      fullfoer: "Fullfør",
      lukk: "Lukk",
      tittel: "Om vitnet",
    },
    nei: "Nei",
    panel: {
      redigerAriaLabel: "Endre vitne {{nr}}",
      slett: "Slett",
      title: "Vitne",
    },
    saaAndre:
      "Vet du om andre vitner som så at det skjedde eller har opplysninger om hendelsen?",
    saaDu: "Så du at hendelsen skjedde?",
    vitne: "Vitne",
    vitneInformasjonOptions: {
      JA: "Ja",
      NEI: "Nei",
    },
  },
  hvordanSkjeddeDet: {
    tittel: "Hvordan skjedde det?",
    infoboksTitle: "Hjelp oss å forstå hvordan det skjedde",
    TYVERI_SKADEVERK_INNBRUDD: {
      infoboks: {
        beskriv: "Beskriv så godt du kan hva som skjedde",
        bulletpoints: [
          "Hva som skjedde før, under og etter hendelsen",
          "Informasjon om fremgangsmåte",
          "Var du i kontakt med den eller de du mener er gjerningsperson?",
          "Hvorfor mener du at gjerningspersonene hadde som hensikt å stjele?",
          "Hvordan stilte gjerningspersonen seg til anklagen?",
          "Var gjerningspersonen voldelig eller truende?",
        ],
        hjelpetekst:
          "Hva som er relevant informasjon varierer fra hendelse til hendelse, og alle detaljer kan være viktige. Typiske ting som er nyttig for oss å vite er:",
      },
      inputLabel: "Beskriv hvordan hendelsen skjedde",
    },
    BEDRAGERI: {
      infoboks: {
        infotekst:
          "Din beskrivelse bør være så utfyllende som mulig. Detaljer er viktig. Skriv gjerne teksten kronologisk og forklar relevant historikk.",
        bulletInfo: "Typiske ting som er nyttig for oss å vite er:",
        bulletpoints: [
          "Hva skjedde før, under og etter hendelsen",
          "Har du i forkant av denne hendelsen fått noen henvendelser der noen ba om personlig informasjon om deg?",
          "Dato for spesifikke hendelser hvis det har pågått over tid",
          "Hva ville gjerningspersonen eller -personene at du skulle gjøre?",
          "Hvordan kommuniserte du med gjerningspersonen eller -personene?",
        ],
        bunnInfo:
          "Du har rett til å la være å oppgi opplysninger som kan bidra til at et nært familiemedlem eller en annen person som står nær deg blir straffet. Du har også rett til å la være å oppgi opplysninger som er et brudd på din taushetsplikt.",
      },
      inputLabel: "Beskriv hele hendelsen fra begynnelse til slutt",
    },
  },
  hvorOgNaar: {
    gjerningssted: {
      radioOptions: {
        ANNEN_ADRESSE: "Søk på adresse eller sted",
        IKKE_FYSISK_STED: "Hendelsen kan ikke knyttes til et fysisk sted",
        UTENLANDSK_ADRESSE: "I utlandet",
        KARTPUNKT: "Velg punkt i kart",
      } satisfies Record<
        Exclude<
          HendelsesstedValg,
          | "FORNAERMET_VIRKSOMHETSADRESSE"
          | "FORNAERMET_FREG_ADRESSE"
          | "ANNET_STED"
        >,
        string
      >,
      annenAdresseEllerSted: {
        annenRadio: "Søk på annen adresse eller sted",
        label: "Søk opp og velg en adresse eller et sted",
        slett: "Slett",
        kartverketFeil: "Fikk ikke kontakt med Kartverket, prøv igjen senere",
        oppslag: {
          forLang: "Søket er ugyldig, maks 100 tegn",
          ulovligeTegn:
            'Unngå følgende spesialtegn i søket : ; ! # % \\ ( ) { } [ ] "',
        },
        adresse: {
          bruksenhetsnummer: {
            label: "Velg bruksenhetsnummer (valgfritt)",
            panelLabel: "Velg bruksenhetsnummer",
          },
        },
        sted: {
          beskrivelse: {
            label: "Beskriv hvor det skjedde",
            helpText:
              "For eksempel stasjonen du gikk av på eller en spesifikk sykkelparkering",
          },
        },
      },
      kart: {
        beskrivelse: {
          label: "Beskriv stedet",
          panelLabel: "Beskrivelse",
        },
        breddegrad: "Breddegrad",
        lengdegrad: "Lengdegrad",
      },
      ikkeFysiskSted: {
        label: "Beskriv hvor det skjedde",
        helpText: "For eksempel sms, nettbutikk-adresse, navngitt dating-app",
        seOverLabel: "Beskriv hvor det skjedde",
      },
      hvor: "Hvor skjedde det?",
      virksomhet: {
        helpText: "Adresse hentet fra enhetsregisteret",
        ingenAdresse: "Ingen adresse på virksomhet",
      },
      freg: {
        helpText: "Adresse hentet fra folkeregisteret",
      },
      utlandet: {
        land: "Søk og velg land",
        postnummer: "Postnummer (valgfritt)",
        poststed: "Sted/By (valgfritt)",
        adresse: "Gatenavn og husnummer (valgfritt)",
      },
    },
    gjerningstidspunkt: {
      fra: {
        label: "Fra dato og klokkeslett",
        helpText: "dd.mm.åååå tt:mm",
      },
      helpText: "Hvis du er usikker kan du oppgi omtrentlig tidspunkt",
      legend: "I hvilket tidsrom skjedde det?",
      naar: "Når skjedde det?",
      til: {
        label: "Til dato og klokkeslett",
        helpText: "dd.mm.åååå tt:mm",
      },
    },
  },
  kvittering: {
    sideHeader: "Kvittering på innsendt anmeldelse",
    forsikring: {
      info1: "Du kan bruke saksnummeret",
      info2: "i kontakt med forsikringsselskapet ditt",
      title: "Anmeldt på grunn av forsikring?",
    },
    header: {
      info: "Trykk her for å se din anmeldelse",
      title: "Saksnummer {{saksnummer}}",
    },
    bedrageriInfo: {
      info: "Svindleren eller svindlerne kan forsøke å lure deg igjen ved å utgi seg for å være noen som skal hjelpe deg med saken (politi, bank eller liknende). Ikke trykk på lenker eller oppgi kode eller passord på sider som du ikke selv har oppsøkt via offentlig nettside eller nettbank.",
      title: "Vær forsiktig",
    },
    hvaSkjerVidere: {
      info: "En oppsummering av anmeldelsen og kvittering på at vi har mottatt den blir sendt til din digitale postkasse eller per post. En saksbehandler vil se på anmeldelsen din og vurdere om politiet mener det er hensiktsmessig å etterforske saken.",
      title: "Hva skjer videre nå?",
      link: "Les mer om hva som skjer når en sak er anmeldt her",
    },
    nyeOpplysninger: {
      title: "Nye opplysninger?",
      info: "Hvis du ønsker å trekke tilbake anmeldelsen eller gi oss flere opplysninger om saken, må du kontakte politidistriktet der hendelsen skjedde og oppgi saksnummeret {{saksnummer}}",
    },
    knapper: {
      gaaTilOversikt: "Gå til oversikten",
      til_oversikten: "Til oversikten",
      opprett: "Opprett ny anmeldelse",
    },
    saknr: "Straffesaksnummer",
    sendtInn: "Sendt inn",
    takk: {
      info: "Anmeldelsen din bidrar til å gi oss oversikt over kriminaliteten i samfunnet vårt. Informasjonen fra anmeldelsen er med på å øke kunnskapen vår og blir brukt til å forebygge at liknende hendelser skjer igjen.",
      title: "Takk for at du valgte å anmelde",
    },
    tittel: "Kvittering",
  },
  navigerTilInnhold: "Hopp til hovedinnhold",
  omAnmelderOgFornaermet: {
    fornaermet: {
      konfliktraad: {
        info: {
          del1: "Det kan være aktuelt å håndtere saken i konfliktrådet. I konfliktrådet møtes gjerningspersonen og fornærmede etter et lovbrudd, og er ofte et effektivt virkemiddel for å forebygge kriminalitet.",
          del2: "Dersom vilkårene for behandling i konflikråd er oppfylt må du kanskje signere en egen samtykkeerklæring senere.",
          lenketekst: "Du kan lese mer om konfliktrådsbehandling her",
        },
        radioOptions: {
          JA: "Ja",
          NEI: "Nei",
          MER_INFO: "Jeg ønsker mer informasjon om konfliktråd",
        } satisfies Record<KonfliktraadSvarType, string>,
        legacyRadioOptions: {
          VET_IKKE: "Vet ikke",
        } satisfies Record<
          Exclude<SkjemaKonfliktraadSvarType, KonfliktraadSvarType>,
          string
        >,
        title: "Konfliktråd",
        fornaermetVilligTilKonfliktraad: {
          label:
            "Er du eller den fornærmede som du anmelder på vegne av villig til å behandle denne saken i konfliktråd?",
        },
      },
      anmelder: {
        seg_selv: "Anmelder på vegne av meg selv",
      },
      fornaermetType: {
        fornaermetTypeOptions: {
          ANMELDER: "Meg selv",
          PERSON: "En annen person",
          VIRKSOMHET: "En bedrift eller organisasjon",
        },
        label: "Hvem anmelder du på vegne av?",
      },
      person: {
        freg: {
          info: "For å kunne legge til en fornærmet person må du oppgi fullt navn og fødselsnummer, og disse opplysningene må stemme overens med hvordan personen er registrert i folkeregisteret",
          navn: {
            label: "Navn",
            helpText:
              "Det er viktig at du skriver fullt navn, slik det er registrert i folkeregisteret",
            valideringsfeil: "Du må oppgi et navn",
          },
          fnr: {
            label: "Fødselsnummer / D-nummer",
            helpText: "Fyll ut 11 siffer, uten mellomrom eller bindestrek",
            valideringsfeil: "Fødselsnummeret må ha nøyaktig 11 siffer",
          },
          submit: {
            label: "Søk i folkeregisteret",
          },
          endre: {
            label: "Forkast og søk igjen",
          },
          error: {
            notFound: {
              title: "Finner ikke treff på navn og fødselsnummer",
              message_zero:
                "Du har søkt feil for mange ganger og har ingen flere forsøk igjen. Prøv igjen senere.",
              message_one:
                "Vi finner ingen personer i folkeregisteret med denne kombinasjonen av navn og fødselsnummer. Pass på at opplysningene stemmer og prøv igjen. Du har {{count}} forsøk igjen",
              message_other:
                "Vi finner ingen personer i folkeregisteret med denne kombinasjonen av navn og fødselsnummer. Pass på at opplysningene stemmer og prøv igjen. Du har {{count}} forsøk igjen",
            },
            forbidden: {
              title: "Ingen flere søk",
              message:
                "Du har søkt feil for mange ganger og har ingen flere forsøk igjen. Prøv igjen senere.",
            },
            unknown: {
              title: "En ukjent feil oppstod",
              message: "En ukjent feil oppstod. Prøv igjen senere.",
            },
          },
        },
        beskrivelse: {
          label: "Andre opplysninger om fornærmede (valgfritt)",
          se_over: "Andre opplysninger om fornærmede",
        },
        epost: {
          label: "E-postadresse (valgfritt)",
          se_over: "E-postadresse",
        },
        etternavn: {
          label: "Etternavn",
        },
        fornavn: {
          label: "Fornavn",
        },
        relasjonstyper: {
          ANNET: "Annet",
          FAMILIE: "Familie",
          VERGE: "Verge",
        },
        telefonnummer: {
          label: "Telefonnummer",
        },
        tilknytning: {
          label: "Hvilken relasjon har du til fornærmede?",
        },
        tilknytningBeskrivelse: {
          label: "Beskriv din relasjon til fornærmede",
          se_over: "Beskrivelse av relasjonen",
        },
      },
      tittel: "Om fornærmede",
      virksomhet: {
        valgtVirksomhet: {
          slett: "Slett",
          beliggenhetsadresse: "Beliggenhetsadresse",
          aktivitet: "Aktivitet",
        },
        orgnummer: "Organisasjonsnummer",
        samtykkeTyper: {
          JA: "Ja",
          NEI: "Nei",
        } satisfies Record<SamtykkeVirksomhetKanSeAnmeldelsen, string>,
        samtykkeVirksomhetKanSeAnmeldelsen: {
          label:
            "Ønsker du å sende en kopi av din anmeldelse til virksomheten?",
          helptext:
            "Kopien inneholder din kontaktinformasjon (ikke fødselsnummer), samt informasjon om stjålne/skadde gjenstander og hvor og når hendelsen skjedde",
        },
        relasjonstyper: {
          ANNET: "Annet",
          ANSATT: "Ansatt",
          LEDER: "Daglig leder eller annen lederrolle",
          VEKTER: "Vekter",
        },
        sokefelt: {
          feilmeldingBrreg:
            "Fikk ikke kontakt med Enhetsregisteret, prøv igjen senere",
          helpText:
            "Søk opp navn eller organisasjonsnummer. Velg underenheten der lovbruddet skjedde",
          hovedenhet: "Hovedenhet",
          label: "Hvilken virksomhet anmelder du for?",
          underenhet: "Underenhet",
        },
        tilknytning: {
          label: "Hva slags tilknytning har du til virksomheten?",
          se_over: "Tilknytning til virksomheten",
        },
        tilknytningBeskrivelse: {
          label: "Beskriv tilknytningen du har til virksomheten",
        },
        tittel: "Om virksomheten",
        vekterSelskap: {
          feilmeldingBrreg:
            "Fikk ikke kontakt med enhetsregisteret, prøv igjen senere",
          helpText: "Søk opp navn eller organisasjonsnummer",
          label: "Hvilket vekterselskap jobber du for?",
          se_over: "Vekterselskap",
        },
        virksomhetensReferanse: {
          label: "Virksomhetens saksreferanse",
          helpText:
            "Referansenummer til virksomhetens interne saksbehandling dersom det er aktuelt",
        },
      },
    },
    om_deg: {
      epost: "E-postadresse",
      etternavn: "Etternavn",
      fnr: "Fødselsnummer / D-nummer",
      fornavn: "Fornavn",
      fregFeil: {
        henteknapp: "Prøv igjen",
        henter: "Henter",
        hjelpetekst: "Prøv igjen eller kom tilbake senere",
        tittel: "Kunne ikke hente navnet ditt fra folkeregisteret",
      },
      hjelpetekst: "Navnet ditt er hentet fra Folkeregisteret.",
      mellomnavn: "Mellomnavn",
      navn: "Navn",
      skatteetaten:
        "Hvis noe er feil, må du endre opplysningene i Folkeregisteret via skatteetaten.no",
      telefon: "Telefonnummer",
      tittel: "Om deg",
    },
    slett_anmeldelse: {
      modal: {
        avbryt: "Avbryt",
        hjelpetekst:
          "Er du sikker på at du vil slette den påbegynte anmeldelsen for godt? Du kan ikke angre.",
        label: "Slette utkast?",
        slett: "Ja, slett",
      },
      slett: "Slett",
    },
  },
  oppsummering: {
    anmeldelse: "Innsendt anmeldelse",
    notifikasjon: {
      info: "Du kan be om innsyn i straffesaksdokumentet ved å ta kontakt med politidistriktet der saken behandles. Saken behandles som regel av det politidistriktet hvor den anmeldte hendelsen skjedde.",
      tittel:
        "Du kan ikke se resten av innholdet i anmeldelsen fordi den utgjør et taushetsbelagt straffesaksdokument",
    },
    skrivUt: "Skriv ut siden",
    saknr: "Straffesaksnummer",
    sendtInn: "Sendt inn",
    tittel: "Innsendt anmeldelse",
    diga: {
      mangler_saksnr: "Saksnummer mangler, ta kontakt med politiet ved behov",
      lovbrudd: {
        label: "Lovbrudd",
        type: {
          TYVERI_AV_SYKKEL: "Tyveri av sykkel",
          TYVERI_AV_MOBILTELEFON: "Tyveri av mobiltelefon",
          TYVERI_FLERE_GJENSTANDER: "Tyveri av andre gjenstander",
          KJENNEMERKE: "Tyveri av kjennemerke (registreringsskilt)",
          SKADEVERK: "Skadeverk på privat eiendom, gjenstander eller kjøretøy",
          KRENKELSE: "Ulovlig bruk av fast eiendom",
          BRUKSTYVERI: "Brukstyveri av kjøretøy eller båt fra privatperson",
        } satisfies Record<DigaAnmeldelseType, string>,
      },
      gjerningssted: {
        type: {
          options: {
            ANNET_OFFENTLIG_STED: "Annet offentlig sted i Norge",
            BUTIKK: "Forretning/butikk i Norge",
            IDRETTS_ELLER_KULTURSTED: "Idretts- og kultursted i Norge",
            KOLLEKTIVTRANSPORT: "Kollektivtransport i Norge",
            OMSORGSINSTITUSJON: "Omsorgsinstitusjon i Norge",
            SERVERINGSSTED: "Restaurant, severingssted i Norge",
            UTLAND: "Hendelsen skjedde i utlandet",
            VED_BOPEL: "Ved eller utenfor bopel i Norge",
          } satisfies Record<GjerningsstedType, string>,
          label: "Type gjerningssted",
        },
        beskrivelse: "Beskrivelse av gjerningssted",
      },
      beskrivelse: "Beskrivelse av hendelse",
      gjenstandBeskrivelse: "Beskrivelse av gjenstanden(e)",
      skadeverk: {
        type: {
          label: "Type skadeverk",
          options: {
            KJORETOY: "Skadeverk på bil, motorsykkel, moped",
            BYGNING: "Skadeverk på eller tilgrisning av bygning",
            KNUST_RUTE: "Skadeverk ved ruteknusing",
            TAGGING: "Skadeverk ved tagging/grafitti",
            BAAT: "Skadeverk på båt",
            ANNET: "Annet skadeverk",
          } satisfies Record<SkadeverkType, string>,
        },
        beskrivelse: "Beskrivelse av skaden",
      },
      brukstyveri: {
        type: {
          label: "Type brukstyveri",
          options: {
            KJORETOY: "Brukstyveri av kjøretøy",
            FARTOY: "Brukstyveri av fartøy",
            ANNET: "Brukstyveri av annet motordrevet kjøretøy eller fartøy",
          } satisfies Record<BrukstyveriType, string>,
        },
        kjoretoyBeskrivelse: "Beskrivelse av kjøretøy",
      },
    },
  },
  personSkjema: {
    legend: "Personalia",
    epost: "E-postadresse",
    etternavn: "Etternavn",
    fjern: "Fjern",
    foedselsdato: {
      helpText: "dd.mm.åååå",
      label: "Fødselsdato",
    },
    foedselsEllerDnummer: {
      helpText: "Fødselsnummer, D-nummer",
      label: "Personlig ID (11 siffer)",
    },
    fornavn: "Fornavn",
    helptext:
      "Du må fylle ut <strong>minst ett</strong> av feltene, men gjerne så mange du kan",
    telefon: "Telefonnummer",
    ekstraKontaktopplysninger: {
      brukernavn: "Brukernavn",
      brukernavnContext: "Hvor ble brukernavnet brukt?",
      brukernavnContextHelptext: "For eksempel Finn.no, Whatsapp",
    },
  },
  se_over: {
    endre_steg: "Endre steg",
    bekreftelseFalskAnmeldelse: {
      checkbox:
        "Jeg er kjent med at det er straffbart å levere en falsk anmeldelse til politiet etter straffelovens §§222 — 225.",
      notifikasjon: "Du må bekrefte at du har forstått ditt ansvar som melder",
    },
    gjerningsperson: {
      gjerningspersoner: "Informasjon om antatt gjerningsperson",
      label: "Har du opplysninger om den eller de du mener har gjort det?",
    },
    hva_har_skjedd: "Hva skjedde?",
    hvordan_skjedde_det: "Hvordan skjedde det?",
    ingen_svar: "ingen svar",
    oppsummering_av_feil: {
      title:
        "Følgende felter må være riktig utfylt før du kan sende inn anmeldelsen:",
    },
    modal: {
      feilet: "Noe feil skjedde ved innsending",
      feiletInfo:
        "Noe feil skjedde og anmeldelsen din ble ikke sendt inn. Prøv igjen, eller kom tilbake senere.",
      lukk: "Lukk",
      provIgjen: "Prøv på nytt",
      senderInn: "Sender inn anmeldelsen...",
      suksess: "Anmeldelsen er sendt inn",
    },
    om_deg: "Om deg",
    om_anmelder: "Om deg",
    om_fornaermede: "Om fornærmede",
    om_virksomheten: "Om virksomheten",
    send: "Send inn anmeldelse",
    tittel: {
      TYVERI_SKADEVERK_INNBRUDD: "Tyveri, innbrudd eller skadeverk",
      BEDRAGERI: "Svindel og ID-tyveri",
    },
    vedlegg: {
      beskrivelse: "Beskrivelse",
      filnavn: "Filnavn",
    },
    vitnerlabel: "Kontaktinformasjon til vitnet",
  },
  skjema_context: {
    lagrer: "Lagrer",
    sender_inn: "Sender inn",
    sist_lagret: "Sist lagret",
    til_oversikten: "Til oversikten",
    tittel: "Anmeldelse",
  },
  skjemaknapper: {
    gaa_tilbake: "Gå til forrige steg",
    gaa_videre: "Gå videre",
  },
  standard: {
    valgfritt: "(valgfritt)",
  },
  steg: {
    erstatningskrav: "Erstatningskrav",
    hva_har_skjedd: "Hva har skjedd?",
    hvem_gjorde_det: "Hvem gjorde det?",
    hvem_saa_det: "Hvem har opplysninger?",
    hvor_og_naar: "Hvor og når skjedde det?",
    konfliktraad: "Behandling i konfliktråd",
    om_deg: "Om deg",
    om_deg_og_fornaermede: "Om deg og fornærmede",
    se_over: "Se over og send inn",
    vedlegg: "Vedlegg",
  },
  vedlegg: {
    beskrivelse: "Gi en kort beskrivelse av filen (valgfritt)",
    wasmSupport:
      "Nettleseren mangler nødvendig funksjonalitet for å laste opp vedlegg. Oppdater nettleseren eller ta i bruk en nyere nettleser, f.eks Google Chrome",
    beskrivelseHjelpetekst:
      "Er filen en video? Oppgi gjerne tidspunkt som er relevant å se på.",
    hjelpetekst:
      "Du kan laste opp inntil {{ maksAntallVedlegg }} filer. Formater som støttes er {{ lovligeFiltyper }}.",
    infotekst: {
      BEDRAGERI: {
        bulletpoints: [
          "Skjermbilder av dialog med svindleren eller svindlerne",
          "Kontoutskrift som viser relevante overføringer",
          "Skjermbilder av aktuelle nettbutikker, annonser eller QR-koder",
          "Lydopptak av telefonsamtale",
          "Anropslogg som viser aktuell telefonsamtale",
        ],
        hjelpetekst:
          "Hvis du har bilde, video eller andre filer som kan bidra til å belyse hva som har skjedd, kan du laste det opp her. Eksempler på vedlegg er:",
      },
      TYVERI_SKADEVERK_INNBRUDD: {
        bulletpoints: [
          "Overvåkningsvideo",
          "Stillbilder eller videoklipp som kan identifisere gjerningspersonen",
          "Bilder av skader",
          "Dokumentasjon på stjålne eller ødelagte gjenstander, for eksempel kvittering",
        ],
        hjelpetekst:
          "Hvis du har bilde, video eller andre filer som kan bidra til å belyse hva som har skjedd, kan du laste det opp her. Eksempler på vedlegg er:",
      },
      label: "Last opp filer (valgfritt)",
    },
    lastOpp: "Last opp filer",
    opplastingFeilet: "Kunne ikke lagre vedlegget",
    slett: "Slett vedlegg",
    status: {
      KLARGJOER: "Klargjør vedlegget for opplasting",
      OPPRETTET: "Klargjør vedlegget for opplasting",
      LASTER_OPP: "Laster opp",
      SCANNER: "Scanner vedlegg for virus",
      FEILET: "Feilet",
      FEILET_FOR_OPPRETTELSE: "Feilet",
      OPPLASTET: "Opplastet",
    },
    tittel: "Last opp vedlegg",
    lukkeUtenOpplasting:
      "Hvis du forlater denne siden vil opplasting av vedlegg bli avbrutt. Er du sikker på at du vil forlate denne siden?",
  },
  samtykkeInformasjon: {
    infotekst: {
      bulletpoints: [
        "For å kunne etterforske svindel og ID-tyveri, kan det bli aktuelt å innhente flere opplysninger og dokumentasjon fra private aktører som banker, kredittselskaper eller låneinstitusjoner. Det kan for eksempel være opplysninger om ditt kundeforhold eller logger knyttet til transaksjoner eller konti oppgitt i denne anmeldelsen",
        "Vi ber derfor om ditt samtykke til å hente inn disse opplysningene. Et samtykke vil innebære at du fritar ansatte i de nevnte selskapene for eventuell taushetsplikt.",
        "Du trenger ikke gi samtykke til innhenting av opplysninger for å kunne levere en anmeldelse.",
        "Du kan når som helst kontakte politiet eller ovennevnte selskap og trekke samtykket, politiet kan likevel velge å beholde de innhentede opplysningene dersom de er nødvendige for etterforskningen.",
      ],
    },
    samtykkeInnhentingAvOpplysninger: {
      ja: "Ja",
      nei: "Nei",
      legend:
        "Samtykker du til at politiet kan innhente opplysninger og dokumentasjon om dine kunde-/kontoforhold?",
    },
  },
};

export default skjematekst;
